/* eslint-disable no-restricted-syntax */
import { faAngleRight } from "@fortawesome/pro-regular-svg-icons";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
    Alert,
    Modal, ModalBody, ModalFooter, ModalHeader
} from "reactstrap";
import { updateCheckState } from "../../../../actions/Checks/actions";
import { Check, Layer, Related, isArtLocation } from "../../../../actions/Layers/constants";
import { stageAllocationRequests } from "../../../../actions/Layers/stageAllocation";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { DeviceResultActions } from "../../../../actions/Tenants/config/constants";
import { CheckLocation, FormField } from "../../../../actions/Tenants/config/constantsTyped";
import {
    useAddLayerAllocationMutation,
    useAddLayerParentMutation,
    useCreateCheckMutation,
    usePatchLayerMutation,
    useRemoveLayerParentMutation,
    useTriggerCheckBusinessRulesMutation
} from "../../../../hooks/mutations";
import { applyLayerField, layerQueryKey } from "../../../../hooks/mutations/usePatchLayerMutation";
import { useLayerLocationHistory, useRouteLayer } from "../../../../hooks/useCurrentEntities";
import { useLayerChecks, useLayerChecksInvalidation } from "../../../../hooks/useLayerChecks";
import { useConnectivity } from "../../../../offline/connectivityMonitor";
import { isOfflineChecksEnabled } from "../../../../offline/db";
import {
    getActiveSessionId,
    getSessionById,
    markReadyToSync,
    setCheckConfigLocationId,
    setStageAllocation
} from "../../../../offline/sessionManager";
import { startOfflineCheck } from "../../../../offline/startOfflineCheck";
import { getFilledArrayOrDefault, isFilledArray } from "../../../../utils";
import { findChanges } from "../../../../utils/findChanges";
import { toast } from "../../../../utils/toast";
import useNavigateFrom from "../../../../utils/useNavigateFrom";
import MetaForm, { metaFormIsValid } from "../../../Forms/MetaForm";
import { Button } from "../../../Helper/Button";

export interface StartCheckFormProps {
    location_config: CheckLocation;
}

interface CreateCheckData {
    [key: string]: any;
}

export const ShowLinkToLocationAlert = ({ location_config, layer }: { location_config: CheckLocation, layer: Layer }) => {
    const config = useConfig();
    if (!location_config.is_linked_to_latest_location) {
        return null;
    }

    const latest_location_id = layer?.latest_location?.location_id;
    if (!latest_location_id) {
        return <div className="pb-3">
            <Alert color="warning"><b>{location_config.text} check</b> should be linked to a location, but <b>{layer.label}</b> is not allocated to a location.</Alert>
        </div>;
    }

    return <div className="pb-3">
        <Alert color="info">This Check will be linked to <b>{config.get_form_option_label("location_id", latest_location_id)}</b></Alert>
    </div>;

};

export default function StartCheckForm({ location_config }: StartCheckFormProps) {
    const [createCheckData, setCheck] = useState<CreateCheckData>({});
    const layer = useRouteLayer();
    // patch update
    const layerIsUpdating = useIsMutating() > 0;
    const checkIsUpdating = useIsMutating() > 0;

    const config = useConfig();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const params = useParams();
    const tenantId = params.tenant_id as string;
    const { isLoading: locationHistoryLoading } = useLayerLocationHistory(tenantId, layer?.id);
    const { isOnline } = useConnectivity();
    const inOfflineMode = isOfflineChecksEnabled(config) && !isOnline;

    // Mutation hooks
    const addAllocationMutation = useAddLayerAllocationMutation();
    const addParentMutation = useAddLayerParentMutation();
    const removeParentMutation = useRemoveLayerParentMutation();
    const patchLayerMutation = usePatchLayerMutation();
    const createCheckMutation = useCreateCheckMutation();
    const triggerBusinessRulesMutation = useTriggerCheckBusinessRulesMutation();

    // * Fetch checks for checking if required checks exist
    const { data: checks = [] } = useLayerChecks(layer?.tenant_id, layer?.id || undefined);

    // * Use hook to invalidate layer checks after mutations
    const { invalidateLayerChecks } = useLayerChecksInvalidation(tenantId, layer?.id ?? undefined);

    const { navigateTo } = useNavigateFrom();
    const [modalOpen, setModal] = useState(params.action === `start-${location_config.value}`);
    const shouldShowModal = isFilledArray(location_config?.meta_form_layer) || isFilledArray(location_config?.pre_check_form);

    // * we use this to copy data from the layer to the check e.g. box_size
    const check_meta_form = getFilledArrayOrDefault(location_config?.pre_check_form).map((field) => {
        // * there is no AVOS Mini device offline, so the check is always manual
        if (inOfflineMode && field.name === "is_manual") {
            return { ...field, disabled: true };
        }
        if (layer[field.default_value_from_layer_key]) {
            return {
                ...field,
                default_value: layer[field.default_value_from_layer_key]
            };
        }
        return field;
    });

    // * drop a "Use AVOS mini" answer given before going offline: force the manual option
    // * (submit turns it into a boolean with Boolean(Number(value)), so manual is the truthy one)
    const isManualField = check_meta_form.find((field) => field.name === "is_manual");
    const manualValue = getFilledArrayOrDefault(isManualField?.options).find((o) => Boolean(Number(o.value)))?.value ?? "1";
    useEffect(() => {
        if (inOfflineMode && isManualField) {
            setCheck((object) => ({ ...object, is_manual: manualValue }));
        }
    }, [inOfflineMode, !!isManualField, manualValue]);

    useEffect(() => {
        if (params.action === `start-${location_config.value}`) {
            setModal(true);
        }
    }, [params.action]);

    const allocateToStage = () => {
        const requests = stageAllocationRequests(location_config.allocate_to_stage, location_config.allocate_parent_of_type, layer);
        for (const request of requests) {
            addAllocationMutation.mutate({ tenantId, ...request });
        }
    };

    const _updateLayerAndStartCheck = async () => {

        if (layerIsUpdating || checkIsUpdating || locationHistoryLoading) {
            return;
        }

        // Offline: only the meta_form_layer fields are greyed out — their values
        // are derived from the layer and can't be edited offline (see the
        // fieldset/MetaForm `disabled={inOfflineMode}` below), so their
        // required-field validation must not block the controller from moving
        // forward. The pre_check_form (rendered as check_meta_form) stays
        // editable offline and its answers are captured into createCheckData /
        // postCheck, so its validation still runs in every mode.
        if (!inOfflineMode && location_config.meta_form_layer && !metaFormIsValid(location_config.meta_form_layer as FormField[], layer)) {
            return;
        }
        if (location_config.pre_check_form && !metaFormIsValid(location_config.pre_check_form as any, createCheckData)) {
            return;
        }

        // Offline the allocation would pause and keep useIsMutating() > 0, which
        // disables the check flow's buttons; it is recorded below and replayed.
        if (!inOfflineMode) {
            allocateToStage();
        }
        let location = location_config.value;

        // For mission...
        // if it is the first ripening check, and there is no first ripening check, go to first ripening check flow
        //
        // Offline note (issue 5.3): the swap depends on the pallet's
        // existing checks list (`layer.checks`), which is loaded from
        // the server and is NOT available offline. The design accepts
        // this as a contained edge case: when offline, the user gets
        // the check type they selected. (The feature is labelled
        // "deprecated" in the config admin form anyway.)
        if (location_config.require_another_check && !inOfflineMode) {
            //  check if one of the checks is first-ripening
            const hasFirstRipeningCheck = checks.find((i) => i.location === location_config.require_another_check);
            // to be backwards compatible
            const hasPreviousRipeningchecks = checks.filter((i) => i.location === location_config.value).length > 0;
            if (!hasFirstRipeningCheck && !hasPreviousRipeningchecks) {
                location = location_config.require_another_check;
            }
        }

        // * Setup the check
        const check: Check = { layer_id: layer.id, location, tenant_id: tenantId } as any;
        check.is_manual = location_config.is_manual || false;

        // * Set the requested_calibration_stage,
        // * needed to fetch and filter calibration fruit
        const { pressure_calibration_settings } = location_config;
        const requested_calibration_stage = pressure_calibration_settings?.calibration_stage || false;
        if (requested_calibration_stage) {
            check.requested_calibration_stage = requested_calibration_stage;
        }

        if (createCheckData?.is_manual) {
            createCheckData.is_manual = Boolean(Number(createCheckData.is_manual));
        }

        if (location_config.is_linked_to_latest_location) {
            check.location_id = layer.latest_location?.location_id;
        }

        const postCheck = { ...check, ...createCheckData };

        if (inOfflineMode) {
            // Offline branch (issue 5.3). Skip the createCheck dispatch
            // entirely — the design's "do NOT dispatch CREATE.SUCCESS"
            // rule (CREATE action types are shared with completeCheck;
            // a synthetic CREATE.SUCCESS could mis-fire reducers).
            // Instead build a synthetic check object and route it
            // through the existing GET.SUCCESS-shaped updateCheckState.
            //
            // Resolve the session id. The scanner flow (CreateLayer →
            // setLayerSelect with checkType) pre-mints one and sets it
            // as active; the search flow (FindLayerBox → autocomplete
            // pick → setLayerSelect without checkType) doesn't, because
            // the check type isn't known until the controller clicks a
            // start-check button on the layer detail page. We also
            // re-mint when the active pointer is stale (points at a
            // previous session for a different pallet, e.g. after the
            // controller scanned A, submitted, then searched for B) —
            // otherwise this check's writes would be tagged onto the
            // wrong session.
            //
            // A session that was already submitted (no longer `collecting`)
            // is also stale: the pointer outlives the submit (see
            // CheckSummary), so starting a second check on the same pallet
            // would otherwise add to the queued one.
            let sessionId: string | null = getActiveSessionId();
            if (sessionId) {
                const existing = await getSessionById(sessionId);
                if (!existing || existing.cached_layer.id !== layer.id || existing.status !== "collecting") {
                    sessionId = null;
                }
            }
            if (!sessionId) {
                const result = await startOfflineCheck({
                    label: layer.label!,
                    layer,
                    checkType: location_config.value,
                    tenantId: config.tenant_id,
                });
                if (!result.ok) {
                    toast.error(result.reason);
                    return;
                }
                sessionId = result.sessionId;
            }
            const tempCheckId = Date.now();
            const syntheticCheck: any = {
                ...postCheck,
                check_id: tempCheckId,
                avocados: [],
                meta: [],
                defects: [],
                // formatCheck2Frontend derives this from `defects` for a server-fetched
                // check; this synthetic check bypasses that formatter, so DefectGroup
                // (which reads check.defect_list directly) would otherwise crash on
                // the summary page with "check.defect_list is not iterable".
                defect_list: [],
                images: [],
            };
            dispatch(updateCheckState(syntheticCheck));
            // The synthetic check has no server row, so the cache is its only source.
            queryClient.setQueryData(["check", tenantId, tempCheckId], syntheticCheck);
            // TODO: Replace Redux updateCheckState with component-level state
            // Persist the location link into the session's check_config so
            // the replay POST /checks carries location_id (online sends it
            // directly). Without this the synced check would silently drop
            // its location. Surface — not swallow — an IDB failure; we don't
            // block the user's flow on it since replay reads the session later.
            if (postCheck.location_id) {
                setCheckConfigLocationId(sessionId, postCheck.location_id).catch((err) => {
                    // eslint-disable-next-line no-console
                    console.error("[offline] failed to persist check location_id", err);
                    toast.error("Could not save this check's location offline; it may not be linked after sync.");
                });
            }
            if (location_config.allocate_to_stage) {
                setStageAllocation(sessionId, {
                    stage: location_config.allocate_to_stage,
                    parent_types: getFilledArrayOrDefault(location_config.allocate_parent_of_type),
                }).catch((err) => {
                    // eslint-disable-next-line no-console
                    console.error("[offline] failed to persist stage allocation", err);
                    toast.error("Could not save the stage allocation offline; the pallet may not be moved after sync.");
                });
            }
            const flowFirstPath = location_config?.flow?.[0]?.path;
            if (flowFirstPath) {
                // Persist the `from` carried into this precheck screen (e.g. ripening room).
                navigateTo(`/layer/${layer.id}/add-check/${tempCheckId}/${flowFirstPath}`, true);
                return;
            }
            // No flow: the pre_check_form was the whole check, so submit it
            // right away — online this case creates the check and runs the
            // business rules (replay does both).
            try {
                await markReadyToSync(sessionId);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error("[offline] markReadyToSync failed", err);
                toast.error("Could not save the check locally. Please try again.");
                return;
            }
            toast.success("Check saved locally. Will sync when connection returns.");
            setModal(false);
            return;
        }

        createCheckMutation.mutate(
            { tenantId, data: postCheck },
            {
                onSuccess: (response) => {
                    const location_config = config.get_check_type_config(layer, response);

                    // This is a feature, not a bug. If no flow is defined we just close the modal.
                    // This way you can setup small checks to just ask small bits of info with pre_check_form
                    if (location_config?.flow?.length === 0) {
                        triggerBusinessRulesMutation.mutate(
                            { check: response },
                            {
                                onSuccess: () => {
                                    invalidateLayerChecks();
                                    setModal(false);
                                },
                            }
                        );
                        return;
                    }
                    // Persist the `from` carried into this precheck screen (e.g. ripening room).
                    navigateTo(`/layer/${layer.id}/add-check/${response.check_id}/${location_config?.flow?.[0]?.path}`, true);
                },
            }
        );
    };

    const isDisabled = () => {
        // We need to use calibration... but calibration is missing
        if (location_config.device_result_action === DeviceResultActions.pressure_advice && !config.is_location_calibrated(location_config, layer)) {
            return true;
        }
        // We need to setup calibration... but is is already calibrated
        if (location_config.device_result_action === DeviceResultActions.setup_pressure_calibration && config.is_location_calibrated(location_config, layer)) {
            return true;
        }
        if (layerIsUpdating || checkIsUpdating || locationHistoryLoading) {
            return true;
        }

        if (String(layer[`start_${location_config.value}_check_disabled`]) === "1") {
            return true;
        }

        return false;
    };
    const startCheck = () => {
        // if it is advance_ripening and there is a previous advance_ripening check, go to that check
        if (isArtLocation(location_config.value)) {
            const prevCheck = checks.sort((a, b) => b.check_id - a.check_id).find((i) => i.location === location_config.value);
            if (prevCheck) {
                navigateTo(`/layer/${layer.id}/add-check/${prevCheck.check_id}/advance_ripening`);
                return;
            }
        }

        // only show modal if extra meta is required on this location
        if (shouldShowModal) {
            setModal(true);
            return;
        }
        _updateLayerAndStartCheck();
    };
    const submit = () => {
        _updateLayerAndStartCheck();
    };


    return (<div>
        <Button color="primary" disabled={isDisabled()} onClick={startCheck} icon={faAngleRight}>{location_config.text}</Button>
        <Modal isOpen={modalOpen} size={"md"} toggle={() => setModal(false)}>
            <ModalHeader toggle={() => setModal(false)}>{location_config.text}</ModalHeader>
            <ModalBody>
                <ShowLinkToLocationAlert location_config={location_config} layer={layer} />
                <div>
                    {isFilledArray(location_config.meta_form_layer) && <>
                        {inOfflineMode && <Alert color="warning">
                            Pallet details can&apos;t be edited offline. Reconnect to make changes. Your check will still be saved.
                        </Alert>}
                        {/* fieldset disabled is a defence-in-depth measure: combined with the MetaForm
                            `disabled` prop, it guarantees every native form control inside is non-
                            interactive even for field types whose component doesn't honour the prop.
                            patchLayer (PATCH layers/{id}) isn't intercepted by the offline write-
                            capture layer, so without this the user could pick a value, see it appear
                            in Redux, then lose it silently. */}
                        <fieldset disabled={inOfflineMode} className="m-0 p-0 border-0">
                            <MetaForm
                                key="layer"
                                meta={location_config.meta_form_layer as any}
                                setValue={(field, value) => {
                                    if (field === "parents") {
                                        const { toBeAdded, toBeRemoved } = findChanges(layer.parents ?? [], value as Related[], (x, y) => x.id === y.id);
                                        for (const parent of toBeAdded) {
                                            addParentMutation.mutate({ layer, parentLayerId: parent.id });
                                        }
                                        for (const parent of toBeRemoved) {
                                            removeParentMutation.mutate({ layer, parentLayerId: parent.id });
                                        }
                                    } else {
                                        // Keystrokes only touch the cached layer; onDebounce owns the write.
                                        queryClient.setQueryData(
                                            layerQueryKey(layer),
                                            (current: any) => applyLayerField(current, field, value)
                                        );
                                    }
                                }}
                                onDebounce={(field, value) => {
                                    if (field !== "parents") {
                                        patchLayerMutation.mutate({ layer, field, value, keep_history: false });
                                    }
                                }}
                                object={layer}
                                disabled={inOfflineMode}
                                config={config} />
                        </fieldset>
                    </>}
                    {isFilledArray(check_meta_form) && <MetaForm
                        key="check"
                        meta={check_meta_form}
                        setValue={(field, value) => setCheck((object) => ({ ...object, [field]: value }))}
                        object={createCheckData}
                        extra_context={{ fruit_variety: layer.fruit_variety || undefined, fruit_type: layer.fruit_type || undefined }}
                        config={config} />}
                </div>
            </ModalBody>
            <ModalFooter>
                <Button color="light" onClick={() => setModal(false)}>Cancel</Button>
                <Button disabled={isDisabled()} color="success" icon={faAngleRight} onClick={submit}>{isFilledArray(location_config?.flow) ? "Start" : "Submit"}</Button>
            </ModalFooter>
        </Modal >
    </div>);
}
